import { useEffect, useMemo, useState, useCallback } from 'react';
import jsPDF from 'jspdf';

// ─── Constants ────────────────────────────────────────────────────────────────

const statusLabels = {
    brouillon: 'Brouillon',
    commandé: 'Commandé',
    'en transit': 'En transit',
    livré: 'Livré',
    reçu: 'Reçu',
    annulé: 'Annulé',
};

const statusBadgeClass = {
    brouillon: 'bg-slate-100 text-slate-600',
    commandé: 'bg-green-100 text-green-700',
    'en transit': 'bg-orange-100 text-orange-700',
    livré: 'bg-blue-100 text-blue-700',
    reçu: 'bg-emerald-100 text-emerald-700',
    annulé: 'bg-rose-100 text-rose-700',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseLocaleNumber = (value) => {
    if (typeof value === 'number') return value;
    const raw = String(value ?? '').trim();
    if (!raw) return NaN;
    const normalized = raw.replace(/[\s\u202f]/g, '');
    if (normalized.includes(',') && normalized.includes('.')) {
        return Number(normalized.replace(/\./g, '').replace(',', '.'));
    }
    if (normalized.includes(',')) {
        return Number(normalized.replace(',', '.'));
    }
    return Number(normalized);
};

const formatLocaleNumber = (value, decimals = 2) =>
    Number(value || 0).toFixed(decimals).replace('.', ',');

// Formatage spécifique PDF : séparateur de milliers = espace (ex: 12 500,00)
const formatPdfNumber = (value, decimals = 2) => {
    const fixed = Number(value || 0).toFixed(decimals);
    const [intPart, decPart] = fixed.split('.');
    const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return decPart !== undefined ? `${withSpaces},${decPart}` : withSpaces;
};

const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

const toDateTimeLocalValue = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const pad = (n) => String(n).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// ─── PDF Helpers (génération 100% programmatique, sans DOM/CSS) ───────────────

const PDF_MARGIN = 14;
const PDF_PAGE_WIDTH = 210;
const PDF_PAGE_HEIGHT = 297;
const PDF_CONTENT_WIDTH = PDF_PAGE_WIDTH - PDF_MARGIN * 2;
const PDF_GREEN = [22, 101, 52];
const PDF_GREEN_LIGHT = [220, 252, 231];
const PDF_GRAY_TEXT = [100, 100, 100];
const PDF_ROW_BORDER = [51, 51, 51];
const SIGNATURE_BOX_WIDTH = 40;
const SIGNATURE_BOX_HEIGHT = 18;

const createPdfDocument = () => new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });

const addPdfLogo = (doc, logoPath) => {
    if (!logoPath) return;
    try {
        const logoX = PDF_MARGIN;
        const logoY = 8;
        const logoWidth = 24;
        const logoHeight = 24;
        // logoDataUrl est maintenant toujours du JPEG (voir loadLogo)
        doc.addImage(logoPath, 'JPEG', logoX, logoY, logoWidth, logoHeight, undefined, 'FAST');
    } catch (err) {
        console.warn('Could not add logo to PDF:', err);
    }
};

const addPdfHeader = (doc, title, infoLines = [], logoPath = null) => {
    // Add logo if available
    if (logoPath) {
        addPdfLogo(doc, logoPath);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...PDF_GREEN);
    const titleX = logoPath ? PDF_MARGIN + 30 : PDF_MARGIN;
    doc.text(title, titleX, 20);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    let y = 35;
    infoLines.forEach((line) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(line.label, PDF_MARGIN, y);
        const labelWidth = doc.getTextWidth(line.label);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(` ${line.value}`, PDF_MARGIN + labelWidth, y);
        y += 5;
    });

    return y + 4;
};

const addPdfFooterNote = (doc, y, text) => {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...PDF_GRAY_TEXT);
    const lines = doc.splitTextToSize(text, PDF_CONTENT_WIDTH);
    doc.text(lines, PDF_MARGIN, y);
};

const addPdfSignatures = (doc, startY, signatures = []) => {
    let signatureY = startY + 4;
    const signatureSpacing = 50;
    let x = PDF_MARGIN;

    signatures.forEach((sig, idx) => {
        // Draw box for signature
        doc.setDrawColor(...PDF_ROW_BORDER);
        doc.rect(x, signatureY, SIGNATURE_BOX_WIDTH, SIGNATURE_BOX_HEIGHT);

        // Label below box
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(50, 50, 50);
        doc.text(sig.label || '', x + 2, signatureY + SIGNATURE_BOX_HEIGHT + 3);
        if (sig.sublabel) {
            doc.setFontSize(7);
            doc.text(sig.sublabel, x + 2, signatureY + SIGNATURE_BOX_HEIGHT + 6);
        }

        x += signatureSpacing;
        if ((idx + 1) % 4 === 0) {
            x = PDF_MARGIN;
            signatureY += SIGNATURE_BOX_HEIGHT + 20;
        }
    });

    return signatureY + SIGNATURE_BOX_HEIGHT + 25;
};

const addPdfTable = (doc, { startY, columns, rows }) => {
    const headerHeight = 8;
    const cellPaddingX = 2;
    const lineHeight = 4.2;
    const fontSize = 8.5;

    let y = startY;

    const drawTableHeader = () => {
        let x = PDF_MARGIN;
        doc.setFillColor(...PDF_GREEN_LIGHT);
        doc.setDrawColor(...PDF_ROW_BORDER);
        doc.rect(PDF_MARGIN, y, PDF_CONTENT_WIDTH, headerHeight, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fontSize);
        doc.setTextColor(...PDF_GREEN);
        columns.forEach((col) => {
            const textX = col.align === 'right' ? x + col.width - cellPaddingX : x + cellPaddingX;
            doc.text(col.header, textX, y + headerHeight / 2 + 1.5, { align: col.align === 'right' ? 'right' : 'left' });
            x += col.width;
        });
        y += headerHeight;
    };

    const ensureSpace = (neededHeight) => {
        if (y + neededHeight > PDF_PAGE_HEIGHT - PDF_MARGIN) {
            doc.addPage();
            y = PDF_MARGIN;
            drawTableHeader();
        }
    };

    drawTableHeader();

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fontSize);
    doc.setTextColor(20, 20, 20);

    rows.forEach((row) => {
        const wrappedCells = columns.map((col, idx) =>
            doc.splitTextToSize(String(row[idx] ?? ''), col.width - cellPaddingX * 2)
        );
        const maxLines = Math.max(1, ...wrappedCells.map((w) => w.length));
        const rowHeight = Math.max(7, maxLines * lineHeight + 2.5);

        ensureSpace(rowHeight);

        let x = PDF_MARGIN;
        doc.setDrawColor(...PDF_ROW_BORDER);
        doc.rect(PDF_MARGIN, y, PDF_CONTENT_WIDTH, rowHeight);

        columns.forEach((col, idx) => {
            const lines = wrappedCells[idx];
            const textX = col.align === 'right' ? x + col.width - cellPaddingX : x + cellPaddingX;
            doc.text(lines, textX, y + 5, { align: col.align === 'right' ? 'right' : 'left' });
            if (idx > 0) doc.line(x, y, x, y + rowHeight);
            x += col.width;
        });

        y += rowHeight;
    });

    return y;
};

const addPdfColumnTotal = (doc, { y, columns, totalColumnIndex, value }) => {
    const rowHeight = 8;
    let x = PDF_MARGIN;

    doc.setFillColor(243, 244, 246);
    doc.setDrawColor(...PDF_ROW_BORDER);
    doc.rect(PDF_MARGIN, y, PDF_CONTENT_WIDTH, rowHeight, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(20, 20, 20);

    columns.forEach((col, idx) => {
        if (idx === totalColumnIndex) {
            doc.text(`${value} Ar`, x + col.width - 2, y + rowHeight / 2 + 1.5, { align: 'right' });
        } else if (idx === totalColumnIndex - 1) {
            doc.text('TOTAL', x + col.width - 2, y + rowHeight / 2 + 1.5, { align: 'right' });
        }
        if (idx > 0) doc.line(x, y, x, y + rowHeight);
        x += col.width;
    });

    return y + rowHeight;
};

const normalizeLineStatusForPdf = (status) => {
    const value = String(status || '').trim().toLowerCase();
    return {
        brouillon: 'brouillon',
        commande: 'commandé',
        commandé: 'commandé',
        'en transit': 'en transit',
        en_transit: 'en transit',
        livré: 'livré',
        livre: 'livré',
        reçu: 'reçu',
        recu: 'reçu',
        annulé: 'annulé',
        annule: 'annulé',
    }[value] || (value || 'commandé');
};

const lineUnitNameForPdf = (line) => line.unit_name || line.unit || 'unité';

const getSupplierDisplayName = (line = {}, suppliers = [], fallback = null) => {
    const source = line || fallback || {};
    if (!source || (typeof source === 'string' && !source.trim())) return '—';

    const directSupplier = source.supplier;
    if (typeof directSupplier === 'string' && directSupplier.trim()) return directSupplier;
    if (directSupplier && typeof directSupplier === 'object') {
        return directSupplier.companyName || directSupplier.company_name || directSupplier.name || directSupplier.supplier_name || directSupplier.label || '—';
    }

    const supplierName = source.supplier_name
        || source.supplierName
        || source.companyName
        || source.company_name
        || source.provider
        || source.provider_name
        || source.name
        || '';

    if (typeof supplierName === 'string' && supplierName.trim()) return supplierName;

    const supplierId = source.supplier_id ?? source.supplierId ?? source.supplierID ?? null;
    if (supplierId && Array.isArray(suppliers) && suppliers.length > 0) {
        const found = suppliers.find((supplier) => String(supplier.id) === String(supplierId));
        if (found) return found.companyName || found.company_name || found.name || '—';
    }

    return source.companyName || source.company_name || source.name || '—';
};

const getLineCompactKey = (line = {}) => {
    const itemType = String(line.itemType || '').trim().toLowerCase() || 'ingredient';
    const itemName = String(line.itemName || '').trim().toLowerCase();
    const targetId = line.targetId ?? line.target_id ?? null;
    return `${itemType}::${targetId ?? ''}::${itemName}`;
};

const compactSupplyLines = (lines = [], { mode = 'sum', keepByGroup = {} } = {}) => {
    const groups = new Map();
    (lines || []).forEach((line) => {
        const key = getLineCompactKey(line);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(line);
    });

    const compacted = [];
    Array.from(groups.entries()).forEach(([key, groupedLines], groupIndex) => {
        if (groupedLines.length <= 1) {
            compacted.push({ ...(groupedLines[0] || {}) });
            return;
        }

        const firstLine = { ...(groupedLines[0] || {}) };
        if (mode === 'keep-one') {
            const selectedIndex = keepByGroup[groupIndex] ?? 0;
            const selectedLine = groupedLines[selectedIndex] || groupedLines[0];
            compacted.push({ ...(selectedLine || {}) });
            return;
        }

        const totalQuantity = groupedLines.reduce((sum, line) => sum + Number(parseLocaleNumber(line.quantity)) || 0, 0);
        const firstPrice = groupedLines.find((line) => line.purchasePrice !== '' && line.purchasePrice !== null && line.purchasePrice !== undefined)?.purchasePrice ?? firstLine.purchasePrice;
        const firstSupplier = getSupplierDisplayName(groupedLines.find((line) => String(line.supplier || '').trim()) || firstLine);
        const firstExpirationDate = groupedLines.find((line) => line.expirationDate)?.expirationDate ?? firstLine.expirationDate;
        const firstDeliveryDate = groupedLines.find((line) => line.actualDeliveryDate)?.actualDeliveryDate ?? firstLine.actualDeliveryDate;

        compacted.push({
            ...firstLine,
            quantity: totalQuantity,
            purchasePrice: firstPrice,
            supplier: firstSupplier,
            expirationDate: firstExpirationDate,
            actualDeliveryDate: firstDeliveryDate,
            perishable: groupedLines.some((line) => line.perishable),
        });
    });

    return compacted;
};

const createEmptyLine = (ingredients = [], units = [], suppliers = [], defaultSupplier = '') => ({
    itemType: 'ingredient',
    targetId: ingredients[0]?.id ?? null,
    itemName: ingredients[0]?.name || '',
    quantity: '',
    unit_id: units[0]?.id ?? null,
    purchasePrice: '',
    perishable: false,
    expirationDate: '',
    supplier: defaultSupplier,
});

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Generic modal shell */
function Modal({ open, onClose, title, subtitle, children, footer, maxWidth = 'max-w-2xl' }) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div className={`relative z-10 flex w-full flex-col rounded-3xl bg-white shadow-2xl ${maxWidth} max-h-[90vh]`}>
                {/* Header */}
                <div className="flex items-start justify-between border-b border-green-100 px-6 py-4">
                    <div>
                        {subtitle && (
                            <p className="mb-0.5 text-xs uppercase tracking-widest text-green-500">{subtitle}</p>
                        )}
                        <h2 className="text-lg font-semibold text-green-900">{title}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="ml-4 rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                        aria-label="Fermer"
                    >
                        ✕
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

                {/* Footer */}
                {footer && (
                    <div className="flex items-center justify-end gap-3 border-t border-green-100 px-6 py-4">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Status badge */
function StatusBadge({ status }) {
    return (
        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusBadgeClass[status] || 'bg-slate-100 text-slate-600'}`}>
            {statusLabels[status] || status}
        </span>
    );
}

/** Field wrapper */
function Field({ label, children }) {
    return (
        <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-green-600">
                {label}
            </label>
            {children}
        </div>
    );
}

const inputCls = 'w-full rounded-2xl border border-green-300 bg-green-50/60 px-4 py-2.5 text-sm text-green-900 placeholder:text-green-400 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200';
const selectCls = inputCls;

/** Error alert */
function ErrorAlert({ messages }) {
    if (!messages?.length) return null;
    return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {messages.map((m, i) => <div key={i}>{m}</div>)}
        </div>
    );
}

// ─── Modal: New / Edit Order ──────────────────────────────────────────────────

function OrderFormModal({
    open,
    onClose,
    draftOrder,
    setDraftOrder,
    onAddLine,
    onEditLine,
    onRemoveLine,
    onValidate,
    onSaveDraft,
    errors,
    draftLineUnitLabel,
    generateReference,
    ingredients = [],
    consumables = [],
    units = [],
    suppliers = [],
}) {
    const [lineModalOpen, setLineModalOpen] = useState(false);
    const [editingLineIndex, setEditingLineIndex] = useState(null);
    const [compactModalOpen, setCompactModalOpen] = useState(false);
    const [compactMode, setCompactMode] = useState('sum');
    const [compactSelections, setCompactSelections] = useState({});

    const duplicateGroups = useMemo(() => {
        const groups = new Map();
        (draftOrder?.lines || []).forEach((line, index) => {
            const key = getLineCompactKey(line);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push({ line, index });
        });
        return Array.from(groups.values()).filter((group) => group.length > 1);
    }, [draftOrder?.lines]);

    const openAddLine = () => { setEditingLineIndex(null); setLineModalOpen(true); };
    const openEditLine = (idx) => { setEditingLineIndex(idx); setLineModalOpen(true); };
    const handleCompactLines = () => {
        const nextLines = compactSupplyLines(draftOrder?.lines || [], {
            mode: compactMode,
            keepByGroup: compactSelections,
        });
        setDraftOrder((prev) => ({ ...prev, lines: nextLines }));
        setCompactModalOpen(false);
        setCompactSelections({});
    };

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                subtitle="Approvisionnement"
                title={draftOrder.reference ? `Bon ${draftOrder.reference}` : 'Nouveau bon de commande'}
                maxWidth="max-w-3xl"
                footer={
                    <>
                        <button
                            type="button"
                            onClick={onSaveDraft}
                            className="rounded-2xl border border-green-300 bg-white px-4 py-2 text-sm font-semibold text-green-700 hover:bg-green-50"
                        >
                            Enregistrer comme brouillon
                        </button>
                        <button onClick={onClose} className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                            Annuler
                        </button>
                        <button onClick={onValidate} className="rounded-2xl bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-500">
                            Valider le bon de commande
                        </button>
                    </>
                }
            >
                <div className="space-y-5">
                    <ErrorAlert messages={errors._global} />

                    {/* Reference & date & supplier */}
                    <div className="grid gap-4 sm:grid-cols-3">
                        <Field label="Service">
                            <select
                                className={selectCls}
                                value={draftOrder.service || ''}
                                onChange={(e) => {
                                    const service = e.target.value || null;
                                    setDraftOrder((p) => ({
                                        ...p,
                                        service,
                                        reference: service ? generateReference(service) : generateReference(null),
                                    }));
                                }}
                            >
                                <option value="">Sélectionnez un service</option>
                                <option value="cuisine">Cuisine</option>
                                <option value="barista">Barista</option>
                            </select>
                        </Field>
                        <Field label="Référence">
                            <input
                                className={inputCls}
                                value={draftOrder.reference}
                                onChange={(e) => setDraftOrder((p) => ({ ...p, reference: e.target.value }))}
                                placeholder="BC-20260625-1"
                            />
                        </Field>
                        <Field label="Date de commande">
                            <input
                                type="date"
                                className={inputCls}
                                value={draftOrder.orderDate}
                                onChange={(e) => setDraftOrder((p) => ({ ...p, orderDate: e.target.value }))}
                            />
                        </Field>
                    </div>

                    {/* Expected delivery date */}
                    <Field label="Date de livraison attendue">
                        <input
                            type="date"
                            className={inputCls}
                            value={draftOrder.expectedDeliveryDate}
                            onChange={(e) => setDraftOrder((p) => ({ ...p, expectedDeliveryDate: e.target.value }))}
                        />
                    </Field>

                    {/* Supplier */}
                    <Field label="Fournisseur par défaut pour ce BC">
                        <select
                            className={selectCls}
                            value={draftOrder.supplier_id || ''}
                            onChange={(e) => setDraftOrder((p) => ({ ...p, supplier_id: Number(e.target.value) || null }))}
                        >
                            <option value="">Aucun (fournisseur par ligne)</option>
                            {suppliers.map((s) => (
                                <option key={s.id} value={s.id}>{s.companyName || s.company_name}</option>
                            ))}
                        </select>
                    </Field>

                    {/* Lines */}
                    <div>
                        <div className="mb-3 flex items-center justify-between">
                            <p className="text-sm font-semibold text-green-800">
                                Lignes du bon
                                {draftOrder.lines.length > 0 && (
                                    <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                                        {draftOrder.lines.length}
                                    </span>
                                )}
                            </p>
                            <div className="flex gap-2">
                                {duplicateGroups.length > 0 && (
                                    <button
                                        onClick={() => {
                                            setCompactSelections({});
                                            setCompactMode('sum');
                                            setCompactModalOpen(true);
                                        }}
                                        className="rounded-2xl border border-green-300 bg-white px-4 py-2 text-xs font-semibold text-green-700 hover:bg-green-50"
                                    >
                                        Compacter
                                    </button>
                                )}
                                <button
                                    onClick={openAddLine}
                                    className="flex items-center gap-1.5 rounded-2xl bg-green-600 px-4 py-2 text-xs font-semibold text-white hover:bg-green-500"
                                >
                                    + Ajouter une ligne
                                </button>
                            </div>
                        </div>

                        {draftOrder.lines.length === 0 ? (
                            <div className="rounded-2xl border-2 border-dashed border-green-200 py-10 text-center">
                                <p className="text-sm text-green-500">Aucune ligne — cliquez sur « Ajouter une ligne »</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-2xl border border-green-200">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-green-50 text-xs uppercase tracking-wide text-green-700">
                                        <tr>
                                            <th className="px-4 py-3 text-left">Produit</th>
                                            <th className="px-4 py-3 text-right">Qté</th>
                                            <th className="px-4 py-3 text-left">Unité</th>
                                            <th className="px-4 py-3 text-right">Prix (Ar)</th>
                                            <th className="px-4 py-3 text-left">Fournisseur</th>
                                            <th className="px-4 py-3" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {draftOrder.lines.map((line, idx) => (
                                            <tr key={idx} className="border-t border-green-100 hover:bg-green-50/60">
                                                <td className="px-4 py-3 font-medium text-slate-800">
                                                    {line.itemName}
                                                    {line.perishable && (
                                                        <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-600">Périssable</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right text-slate-800">{formatLocaleNumber(line.quantity, 0)}</td>
                                                <td className="px-4 py-3 text-slate-800">{draftLineUnitLabel(line)}</td>
                                                <td className="px-4 py-3 text-right text-slate-800">{formatLocaleNumber(line.purchasePrice, 2)}</td>
                                                <td className="px-4 py-3 text-slate-800">{getSupplierDisplayName(line, suppliers)}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => openEditLine(idx)}
                                                            className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 hover:bg-green-200"
                                                        >
                                                            Modifier
                                                        </button>
                                                        <button
                                                            onClick={() => onRemoveLine(idx)}
                                                            className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-200"
                                                        >
                                                            Supprimer
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            {compactModalOpen && (
                <Modal
                    open={compactModalOpen}
                    onClose={() => setCompactModalOpen(false)}
                    title="Compacter les lignes"
                    subtitle="Bon de commande"
                    maxWidth="max-w-lg"
                    footer={
                        <>
                            <button onClick={() => setCompactModalOpen(false)} className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                                Annuler
                            </button>
                            <button onClick={handleCompactLines} className="rounded-2xl bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-500">
                                Appliquer
                            </button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <p className="text-sm text-slate-600">
                            Les lignes identiques peuvent être regroupées en une seule ligne. Vous pouvez soit additionner les quantités, soit conserver une seule occurrence.
                        </p>
                        <Field label="Mode de regroupement">
                            <select className={selectCls} value={compactMode} onChange={(e) => setCompactMode(e.target.value)}>
                                <option value="sum">Additionner les quantités</option>
                                <option value="keep-one">Garder 1 seule ligne</option>
                            </select>
                        </Field>
                        {compactMode === 'keep-one' && duplicateGroups.length > 0 && (
                            <div className="space-y-3">
                                {duplicateGroups.map((group, groupIndex) => (
                                    <div key={`${group[0].key ?? groupIndex}`} className="rounded-2xl border border-green-200 bg-green-50/70 p-3">
                                        <p className="mb-2 text-sm font-semibold text-green-800">
                                            {group[0].line.itemName || 'Produit'}
                                        </p>
                                        <select
                                            className={selectCls}
                                            value={compactSelections[groupIndex] ?? 0}
                                            onChange={(e) => setCompactSelections((prev) => ({ ...prev, [groupIndex]: Number(e.target.value) }))}
                                        >
                                            {group.map((entry, idx) => (
                                                <option key={`${entry.index}-${idx}`} value={idx}>
                                                    Ligne {idx + 1} — Qté {formatLocaleNumber(entry.line.quantity, 0)} — {getSupplierDisplayName(entry.line) || 'Sans fournisseur'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {/* Line sub-modal */}
            {lineModalOpen && (
                <LineFormModal
                    open={lineModalOpen}
                    onClose={() => setLineModalOpen(false)}
                    editingIndex={editingLineIndex}
                    existingLine={editingLineIndex !== null ? draftOrder.lines[editingLineIndex] : null}
                    defaultSupplier={draftOrder.supplier_id ? suppliers.find((s) => Number(s.id) === Number(draftOrder.supplier_id))?.companyName || suppliers.find((s) => Number(s.id) === Number(draftOrder.supplier_id))?.company_name || '' : ''}
                    onSave={(line) => {
                        if (editingLineIndex !== null) onEditLine(editingLineIndex, line);
                        else onAddLine(line);
                        setLineModalOpen(false);
                    }}
                    ingredients={ingredients}
                    consumables={consumables}
                    units={units}
                    suppliers={suppliers}
                />
            )}
        </>
    );
}

// ─── Modal: Line Form ─────────────────────────────────────────────────────────

function LineFormModal({ open, onClose, editingIndex, existingLine, onSave, ingredients = [], consumables = [], units = [], suppliers = [], defaultSupplier = '' }) {
    const [form, setForm] = useState(
        existingLine
            ? { ...existingLine, quantity: String(existingLine.quantity), purchasePrice: String(existingLine.purchasePrice) }
            : createEmptyLine(ingredients, units, suppliers, defaultSupplier)
    );
    const [err, setErr] = useState(null);
    const [priceManuallyEdited, setPriceManuallyEdited] = useState(Boolean(existingLine));
    const [suggestedPrice, setSuggestedPrice] = useState('');
    const [suggestedUnitPrice, setSuggestedUnitPrice] = useState(null);

    const availableItems = useMemo(() => {
        const source = form.itemType === 'consumable' ? consumables : ingredients;
        const list = Array.isArray(source) ? source : [];

        return list
            .filter((item) => {
                if (form.itemType === 'consumable') {
                    return !item?.item_type || item.item_type === 'consumable';
                }

                return !item?.item_type || item.item_type === 'ingredient';
            })
            .filter((item, index, arr) => arr.findIndex((candidate) => String(candidate.id) === String(item.id)) === index)
            .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
    }, [consumables, ingredients, form.itemType]);

    const selectedItem = useMemo(
        () => availableItems.find((item) => item.id === Number(form.targetId)),
        [availableItems, form.targetId]
    );

    useEffect(() => {
        if (!availableItems?.length) return;
        if (selectedItem) return;
        const name = (form.itemName || '').trim();
        if (!name) return;
        const match = availableItems.find((item) => String(item.id) === String(form.targetId) || item.name === name || (item.name || '').toLowerCase() === name.toLowerCase());
        if (match) {
            setForm((p) => ({ ...p, targetId: match.id, unit_id: p.unit_id || match.unit_id || match.unit?.id || p.unit_id }));
        }
    }, [availableItems, form.itemName, form.targetId, selectedItem]);

    useEffect(() => {
        if (!selectedItem) return;
        const lastPrice = Number(selectedItem.last_price ?? selectedItem.lastPrice ?? selectedItem.purchasePrice ?? selectedItem.cost_per_unit ?? 0);
        const quantity = Number(parseLocaleNumber(form.quantity) || 0);
        const totalEstimation = quantity > 0 ? lastPrice * quantity : '';
        setSuggestedUnitPrice(lastPrice > 0 ? lastPrice : null);
        setSuggestedPrice(totalEstimation ? formatLocaleNumber(totalEstimation, 2) : '');

        if (!priceManuallyEdited && totalEstimation) {
            setForm((p) => ({ ...p, purchasePrice: String(formatLocaleNumber(totalEstimation, 2)) }));
        }
    }, [selectedItem, form.quantity, priceManuallyEdited]);

    useEffect(() => {
        if (form.itemType === 'ingredient' && selectedItem?.unit_id) {
            setForm((p) => ({ ...p, unit_id: selectedItem.unit_id }));
        }
    }, [form.itemType, selectedItem]);

    useEffect(() => {
        if (form.itemType === 'ingredient' && selectedItem?.name && !form.itemName.trim()) {
            setForm((p) => ({ ...p, itemName: selectedItem.name }));
        }
    }, [form.itemType, selectedItem]);

    useEffect(() => {
        // When editing an existing line, try to map its itemName or targetId to a known ingredient
        if (!existingLine) return;
        if (form.itemType !== 'ingredient') return;
        // Only map when form.targetId is empty/null/undefined
        if (form.targetId !== null && form.targetId !== undefined && form.targetId !== '') return;
        const match = ingredients.find((i) => String(i.id) === String(existingLine.targetId) || i.name === existingLine.itemName);
        if (match) {
            setForm((p) => ({
                ...p,
                targetId: match.id,
                unit_id: p.unit_id || match.unit_id || match.unit?.id || p.unit_id,
            }));
        }
    }, [existingLine, ingredients]);

    const handleSave = (event) => {
        event?.preventDefault();

        const itemName = form.itemName.trim() || selectedItem?.name || '';
        const quantity = parseLocaleNumber(form.quantity);
        const purchasePrice = form.purchasePrice === '' ? null : parseLocaleNumber(form.purchasePrice);
        const supplierName = form.supplier?.trim() || '';

        if (!itemName) {
            setErr('Le nom du produit est requis.');
            return;
        }
        if (!quantity || Number.isNaN(quantity) || quantity <= 0) {
            setErr('La quantité doit être un nombre supérieur à 0.');
            return;
        }
        if (purchasePrice !== null && (Number.isNaN(purchasePrice) || purchasePrice < 0)) {
            setErr('Le prix d’achat doit être un nombre valide supérieur ou égal à 0.');
            return;
        }
        if (!supplierName) {
            setErr('Le fournisseur est requis.');
            return;
        }

        setErr(null);
        onSave({
            itemType: form.itemType,
            targetId: form.itemType === 'ingredient' && Number(form.targetId) > 0 ? Number(form.targetId) : null,
            itemName,
            quantity,
            unit_id: Number(form.unit_id) || null,
            unit: form.itemType === 'ingredient'
                ? selectedItem?.unit?.name || selectedItem?.unit || form.unit || ''
                : units.find((u) => u.id === Number(form.unit_id))?.name || form.unit || '',
            purchasePrice,
            perishable: form.perishable,
            expirationDate: form.expirationDate || null,
            supplier: supplierName,
        });
    };

    const handlePriceChange = (value) => {
        setPriceManuallyEdited(true);
        setForm((p) => ({ ...p, purchasePrice: value }));
    };

    

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={editingIndex !== null ? 'Modifier la ligne' : 'Ajouter une ligne'}
            maxWidth="max-w-lg"
            footer={
                <>
                    <button onClick={onClose} className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                        Annuler
                    </button>
                    <button type="button" onClick={handleSave} className="rounded-2xl bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-500">
                        {editingIndex !== null ? 'Mettre à jour' : 'Ajouter'}
                    </button>
                </>
            }
        >
            <div className="space-y-4">
                {err && <ErrorAlert messages={[err]} />}

                <Field label="Type de produit">
                    <select className={selectCls} value={form.itemType} onChange={(e) => setForm((p) => ({ ...p, itemType: e.target.value }))}>
                        <option value="ingredient">Ingrédient</option>
                        <option value="consumable">Consommable</option>
                        <option value="dish">Plat direct</option>
                    </select>
                </Field>

                <Field label="Nom du produit">
                    {form.itemType === 'ingredient' || form.itemType === 'consumable' ? (
                        // Show dropdown when possible; if the current itemName doesn't match any ingredient,
                        // include a "Personnalisé" option so edit shows a select like creation.
                        <select
                            className={selectCls}
                            value={form.targetId ?? ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === 'custom') {
                                    setForm((p) => ({ ...p, targetId: null }));
                                    return;
                                }
                                if (val === '') {
                                    setForm((p) => ({ ...p, targetId: null, itemName: '' }));
                                    return;
                                }
                                const id = Number(val);
                                const item = availableItems.find((entry) => entry.id === id);
                                setForm((p) => ({ ...p, targetId: id, itemName: item?.name || p.itemName }));
                            }}
                        >
                            <option value="">Sélectionner un {form.itemType === 'consumable' ? 'consommable' : 'ingrédient'}</option>
                            {availableItems.map((item) => (
                                <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                            {(!availableItems.find((item) => item.name === form.itemName) && form.itemName) && (
                                <option value="custom">Personnalisé — {form.itemName}</option>
                            )}
                        </select>
                    ) : (
                        <input
                            className={inputCls}
                            placeholder="Ex. Farine de blé"
                            value={form.itemName}
                            onChange={(e) => setForm((p) => ({ ...p, itemName: e.target.value }))}
                        />
                    )}
                </Field>

                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Quantité">
                        <input
                            type="text"
                            className={inputCls}
                            value={form.quantity}
                            onChange={(e) => setForm((p) => ({ ...p, quantity: e.target.value }))}
                        />
                    </Field>
                    <Field label="Unité">
                        {form.itemType === 'ingredient' || form.itemType === 'consumable' ? (
                            <div className={`${inputCls} cursor-default`}>
                                {selectedItem?.unit?.name || selectedItem?.unit || '—'}
                            </div>
                        ) : (
                            <select className={selectCls} value={form.unit_id ?? ''} onChange={(e) => setForm((p) => ({ ...p, unit_id: Number(e.target.value) }))}>
                                {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                        )}
                    </Field>
                </div>

                <Field label="Prix d'achat (Ar)">
                    <input
                        type="text"
                        className={inputCls}
                        placeholder="0,00"
                        value={form.purchasePrice}
                        onChange={(e) => handlePriceChange(e.target.value)}
                    />
                    {suggestedUnitPrice != null && !priceManuallyEdited && form.quantity && (
                        <p className="mt-2 text-xs text-slate-500">
                            Prix estimé : {formatLocaleNumber(suggestedUnitPrice, 2)} Ar/unité × {formatLocaleNumber(form.quantity, 0)} = {suggestedPrice} Ar
                        </p>
                    )}
                    {suggestedUnitPrice != null && priceManuallyEdited && (
                        <p className="mt-2 text-xs text-slate-400">
                            Prix estimé (non appliqué) : {formatLocaleNumber(suggestedUnitPrice, 2)} Ar/unité × {formatLocaleNumber(form.quantity, 0)} = {suggestedPrice} Ar
                        </p>
                    )}
                </Field>

                <Field label="Fournisseur">
                    <div className="space-y-2">
                        {suppliers.length > 0 && (
                            <select
                                className={selectCls}
                                value={form.supplier || ''}
                                onChange={(e) => setForm((p) => ({ ...p, supplier: e.target.value }))}
                            >
                                <option value="">Sélectionner un fournisseur</option>
                                {suppliers.map((s) => (
                                    <option key={s.id} value={s.companyName || s.company_name || ''}>
                                        {s.companyName || s.company_name || ''}
                                    </option>
                                ))}
                            </select>
                        )}
                        <input
                            type="text"
                            className={inputCls}
                            placeholder="Nom du fournisseur"
                            value={form.supplier}
                            onChange={(e) => setForm((p) => ({ ...p, supplier: e.target.value }))}
                        />
                    </div>
                </Field>

                <label className="flex cursor-pointer items-center gap-3">
                    <input
                        type="checkbox"
                        checked={form.perishable}
                        onChange={(e) => setForm((p) => ({ ...p, perishable: e.target.checked }))}
                        className="h-4 w-4 rounded border-green-300 text-green-600"
                    />
                    <span className="text-sm text-green-700">Produit périssable</span>
                </label>

                {form.perishable && (
                    <Field label="Date d'expiration">
                        <input
                            type="date"
                            className={inputCls}
                            value={form.expirationDate}
                            onChange={(e) => setForm((p) => ({ ...p, expirationDate: e.target.value }))}
                        />
                    </Field>
                )}
            </div>
        </Modal>
    );
}

// ─── Modal: Receive ───────────────────────────────────────────────────────────

function ReceiveModal({ open, onClose, group, receiveLines, onUpdateLine, onSubmit, errors }) {
    const canMarkReceived = (line) =>
        line.receivedQuantity !== '' &&
        Number(parseLocaleNumber(line.receivedQuantity)) > 0 &&
        line.purchasePrice !== '' &&
        !Number.isNaN(parseLocaleNumber(line.purchasePrice)) &&
        Boolean(line.actualDeliveryDate);

    return (
        <Modal
            open={open}
            onClose={onClose}
            subtitle="Réceptionner le bon"
            title={group?.reference || ''}
            maxWidth="max-w-3xl"
            footer={
                <>
                    <div className="mr-auto text-sm text-rose-600">{errors._global?.[0] || ''}</div>
                    <button onClick={onClose} className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                        Annuler
                    </button>
                    <button onClick={onSubmit} className="rounded-2xl bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-500">
                        Enregistrer les réceptions
                    </button>
                </>
            }
        >
            <div className="space-y-4">
                {receiveLines.map((line, idx) => (
                    <div key={line.id} className={`rounded-2xl border p-4 transition ${line.status === 'reçu' ? 'border-green-300 bg-green-50' : line.status === 'annulé' ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-white'}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <p className="font-semibold text-slate-800">{line.itemName}</p>
                                <p className="text-xs text-slate-500">Commandé : {formatLocaleNumber(line.desiredQuantity || 0, 0)}</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => onUpdateLine(idx, 'status', 'reçu')}
                                    className={`rounded-full px-3 py-1 text-xs font-semibold transition ${line.status === 'reçu' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800 hover:bg-green-200'}`}
                                >
                                    ✓ Reçu
                                </button>
                                <button
                                    onClick={() => onUpdateLine(idx, 'status', 'annulé')}
                                    className={`rounded-full px-3 py-1 text-xs font-semibold transition ${line.status === 'annulé' ? 'bg-rose-600 text-white' : 'bg-rose-100 text-rose-700 hover:bg-rose-200'}`}
                                >
                                    ✕ Annuler
                                </button>
                            </div>
                        </div>

                        {line.status === 'reçu' && (
                            <div className="mt-3 grid gap-3 sm:grid-cols-4">
                                <Field label="Qté reçue">
                                    <input
                                        type="number"
                                        step="0.001"
                                        className={inputCls}
                                        value={line.receivedQuantity}
                                        onChange={(e) => onUpdateLine(idx, 'receivedQuantity', e.target.value)}
                                    />
                                </Field>
                                <Field label="Prix reçu (Ar)">
                                    <input
                                        type="text"
                                        className={inputCls}
                                        value={line.purchasePrice}
                                        onChange={(e) => onUpdateLine(idx, 'purchasePrice', e.target.value)}
                                    />
                                </Field>
                                <Field label="Date et heure réception">
                                    <input
                                        type="datetime-local"
                                        className={inputCls}
                                        value={line.actualDeliveryDate}
                                        onChange={(e) => onUpdateLine(idx, 'actualDeliveryDate', e.target.value)}
                                    />
                                </Field>
                                <Field label="Fournisseur">
                                    <input
                                        type="text"
                                        className={inputCls}
                                        value={line.supplier || ''}
                                        onChange={(e) => onUpdateLine(idx, 'supplier', e.target.value)}
                                        placeholder="Nom du fournisseur"
                                    />
                                </Field>
                            </div>
                        )}

                        {line.status === 'reçu' && !canMarkReceived(line) && (
                            <p className="mt-2 text-xs text-rose-600">Complétez la quantité, le prix et la date pour valider cette ligne.</p>
                        )}
                    </div>
                ))}
            </div>
        </Modal>
    );
}

// ─── Modal: Detail ────────────────────────────────────────────────────────────

function DetailModal({ open, onClose, group, orderGroupStatus, lineUnitName, onReceive, onValidateDraft, onUpdateSupplyOrder, onSaveSupplyOrder, onDeleteSupplyOrder, ingredients = [], consumables = [], units = [], suppliers = [] }) {
    const [currentGroup, setCurrentGroup] = useState(group);
    const [editMode, setEditMode] = useState(false);
    const [editedGroup, setEditedGroup] = useState(null);
    const [lineModalOpen, setLineModalOpen] = useState(false);
    const [editingLineIndex, setEditingLineIndex] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [compactModalOpen, setCompactModalOpen] = useState(false);
    const [compactMode, setCompactMode] = useState('sum');
    const [compactSelections, setCompactSelections] = useState({});

    useEffect(() => {
        if (!open) {
            setEditMode(false);
            setEditedGroup(null);
            setLineModalOpen(false);
            setEditingLineIndex(null);
            setSaveError(null);
            return;
        }
        if (group) setCurrentGroup(group);
    }, [open, group]);

    const enterEditMode = () => {
        const groupData = currentGroup || group;
        // Normaliser les données depuis le backend (underscores -> camelCase) au niveau du groupe ET des lignes
        const normalizedLines = (groupData?.lines || []).map(line => ({
            ...line,
            // Convertir les champs snake_case en camelCase pour la cohérence
            itemType: line.item_type || line.itemType || '',
            targetId: line.target_id ?? line.targetId ?? null,
            itemName: line.item_name || line.itemName || '',
            unitId: line.unit_id ?? line.unitId ?? null,
            purchasePrice: line.purchase_price ?? line.purchasePrice ?? '',
            supplierId: line.supplier_id ?? line.supplierId ?? null,
            orderDate: line.order_date || line.orderDate || '',
            expectedDeliveryDate: line.expected_delivery_date || line.expectedDeliveryDate || '',
            purchaseDate: line.purchase_date || line.purchaseDate || '',
            expirationDate: line.expiration_date || line.expirationDate || '',
            receivedQuantity: line.received_quantity ?? line.receivedQuantity ?? null,
            actualDeliveryDate: line.actual_delivery_date || line.actualDeliveryDate || '',
            validatedByUserId: line.validated_by_user_id ?? line.validatedByUserId ?? null,
            validatedAt: line.validated_at || line.validatedAt || '',
            receivedByUserId: line.received_by_user_id ?? line.receivedByUserId ?? null,
            receivedAt: line.received_at || line.receivedAt || '',
        }));
        
        const normalizedGroup = {
            ...groupData,
            orderDate: groupData.order_date || groupData.orderDate || '',
            expectedDeliveryDate: groupData.expected_delivery_date || groupData.expectedDeliveryDate || '',
            lines: normalizedLines
        };
        setEditedGroup(normalizedGroup);
        setEditMode(true);
    };

    const exitEditMode = () => {
        setEditMode(false);
        setEditedGroup(null);
        setSaveError(null);
    };

    const getLineSignature = (line = {}) => JSON.stringify({
        itemType: line.itemType || 'ingredient',
        targetId: line.targetId ?? null,
        itemName: line.itemName || '',
        quantity: line.quantity ?? '',
        unit_id: line.unit_id ?? null,
        unit: line.unit || '',
        purchasePrice: line.purchasePrice ?? '',
        supplier: line.supplier || '',
        perishable: !!line.perishable,
        expirationDate: line.expirationDate || null,
        receivedQuantity: line.receivedQuantity ?? null,
        actualDeliveryDate: line.actualDeliveryDate || null,
        status: line.status || '',
        lotNumber: line.lotNumber || null,
        orderDate: line.orderDate || null,
    });

    const handleSaveChanges = async () => {
        if (!editedGroup) return;

        try {
            setSaving(true);
            setSaveError(null);

            const reference = editedGroup.reference || group.reference;
            const orderDate = editedGroup.orderDate || group.orderDate || null;
            const expectedDeliveryDate = editedGroup.expectedDeliveryDate?.trim() || null;
            const defaultGroupStatus = orderGroupStatus(currentGroup || group);
            const originalLines = ((currentGroup || group)?.lines || []).map((line) => ({ ...line }));
            const savedLines = [];

            // Déterminer les IDs des lignes qui ont disparu après compaction
            const currentLineIds = new Set(
                (editedGroup.lines || [])
                    .map((line) => line.id)
                    .filter((id) => id)
            );
            const linesToDelete = originalLines.filter((line) => line.id && !currentLineIds.has(line.id));

            // Supprimer les lignes qui ont disparu
            for (const line of linesToDelete) {
                if (typeof onDeleteSupplyOrder === 'function') {
                    try {
                        await onDeleteSupplyOrder(line.id);
                    } catch (err) {
                        console.warn(`Failed to delete line ${line.id}:`, err);
                    }
                }
            }

            for (const line of editedGroup.lines || []) {
                const originalLine = line.id
                    ? originalLines.find((item) => String(item.id) === String(line.id))
                    : null;
                const isUnchanged = !!originalLine && getLineSignature(line) === getLineSignature(originalLine);

                if (isUnchanged) {
                    savedLines.push(originalLine);
                    continue;
                }

                const payload = {
                    ...line,
                    lotNumber: reference,
                    orderDate,
                    expectedDeliveryDate,
                    status: line.status || (defaultGroupStatus === 'brouillon' ? 'brouillon' : defaultGroupStatus === 'reçu' ? 'reçu' : 'commandé'),
                };

                if (line.id) {
                    if (typeof onUpdateSupplyOrder === 'function') {
                        await onUpdateSupplyOrder(line.id, payload);
                    }
                    savedLines.push(payload);
                    continue;
                }

                if (typeof onSaveSupplyOrder === 'function') {
                    const createdLine = await onSaveSupplyOrder(payload);
                    savedLines.push(createdLine || payload);
                    continue;
                }

                savedLines.push(payload);
            }

            setEditedGroup((prev) => (prev ? { ...prev, lines: savedLines } : prev));
            setCurrentGroup((prev) => (prev ? { ...prev, lines: savedLines } : prev));
            setEditMode(false);
            setEditingLineIndex(null);
            setLineModalOpen(false);
        } catch (err) {
            console.error(err);
            setSaveError(err?.message || 'Impossible d’enregistrer les modifications.');
        } finally {
            setSaving(false);
        }
    };

    const duplicateGroups = useMemo(() => {
        const groups = new Map();
        ((editedGroup?.lines || [])).forEach((line, index) => {
            const key = getLineCompactKey(line);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push({ line, index });
        });
        return Array.from(groups.values()).filter((group) => group.length > 1);
    }, [editedGroup?.lines]);

    if (!group) return null;
    const state = orderGroupStatus(currentGroup || group);

    const openAddLine = () => { 
        setEditingLineIndex(null); 
        setLineModalOpen(true); 
    };

    const openEditLine = (idx) => { 
        setEditingLineIndex(idx); 
        setLineModalOpen(true); 
    };

    const handleCompactLines = () => {
        setEditedGroup((prev) => ({
            ...prev,
            lines: compactSupplyLines(prev?.lines || [], {
                mode: compactMode,
                keepByGroup: compactSelections,
            }),
        }));
        setCompactModalOpen(false);
        setCompactSelections({});
    };

    const handleAddLine = (line) => {
        setEditedGroup((p) => ({ ...p, lines: [...(p?.lines || []), line] }));
        setLineModalOpen(false);
    };

    const handleEditLine = (idx, line) => {
        setEditedGroup((p) => {
            const lines = [...(p?.lines || [])];
            lines[idx] = line;
            return { ...p, lines };
        });
        setLineModalOpen(false);
    };

    const handleRemoveLine = (idx) => {
        setEditedGroup((p) => ({ ...p, lines: (p?.lines || []).filter((_, i) => i !== idx) }));
    };

    const draftLineUnitLabel = (line) => {
        if (line.itemType === 'ingredient') {
            const ing = ingredients.find((i) => i.id === Number(line.targetId));
            return ing?.unit?.name || ing?.unit || 'unité';
        }
        return units.find((u) => u.id === Number(line.unit_id))?.name || 'unité';
    };

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                subtitle="Bon de commande"
                title={group.reference}
                maxWidth="max-w-[90vw]"
                footer={
                editMode ? (
                    <>
                        <button
                            onClick={exitEditMode}
                            className="rounded-2xl border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleSaveChanges}
                            disabled={saving}
                            className="rounded-2xl bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
                        </button>
                    </>
                ) : (
                    <>
                        {state === 'brouillon' && (
                            <>
                                <button
                                    onClick={enterEditMode}
                                    className="rounded-2xl border border-green-300 bg-white px-5 py-2 text-sm font-semibold text-green-700 hover:bg-green-50"
                                >
                                    Modifier
                                </button>
                                <button
                                    onClick={() => { 
                                        onValidateDraft(currentGroup || group);
                                        onClose();
                                    }}
                                    className="rounded-2xl bg-orange-600 px-5 py-2 text-sm font-semibold text-white hover:bg-orange-500"
                                >
                                    Valider la commande
                                </button>
                            </>
                        )}
                        {state !== 'reçu' && state !== 'brouillon' && (currentGroup || group).lines.some((l) => !['reçu', 'annulé'].includes(l.status)) && (
                            <button
                                onClick={() => { onClose(); onReceive(currentGroup || group); }}
                                className="rounded-2xl bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-500"
                            >
                                Réceptionner
                            </button>
                        )}
                        <button onClick={onClose} className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                            Fermer
                        </button>
                    </>
                )
            }
        >
            <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p className="text-sm text-slate-500">
                            {(editMode ? editedGroup : currentGroup)?.orderDate
                                ? `Commande du ${(editMode ? editedGroup : currentGroup).orderDate}`
                                : group.orderDate ? `Commande du ${group.orderDate}` : 'Date non renseignée'}
                        </p>
                        {(editMode ? editedGroup : currentGroup)?.expectedDeliveryDate && (
                            <p className="text-sm text-slate-500">
                                Livraison attendue : {(editMode ? editedGroup : currentGroup).expectedDeliveryDate}
                            </p>
                        )}
                    </div>
                    <StatusBadge status={state} />
                </div>

                {saveError && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        {saveError}
                    </div>
                )}

                {editMode && editedGroup && (
                    <div className="mb-4 rounded-2xl border border-blue-200 bg-blue-50 p-4">
                        <p className="mb-3 font-semibold text-blue-800">Informations de la commande</p>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field label="Date de commande">
                                <input
                                    type="date"
                                    className={inputCls}
                                    value={editedGroup.orderDate || ''}
                                    onChange={(e) => setEditedGroup((p) => ({ ...p, orderDate: e.target.value }))}
                                />
                            </Field>
                            <Field label="Date de livraison attendue">
                                <input
                                    type="date"
                                    className={inputCls}
                                    value={editedGroup.expectedDeliveryDate || ''}
                                    onChange={(e) => setEditedGroup((p) => ({ ...p, expectedDeliveryDate: e.target.value }))}
                                />
                            </Field>
                        </div>
                    </div>
                )}

                {editMode && editedGroup && (
                    <div className="mb-4 rounded-2xl border border-green-200 bg-green-50 p-4">
                        <div className="mb-3 flex items-center justify-between">
                            <div>
                                <p className="font-semibold text-green-800">Lignes du bon</p>
                                <p className="text-xs text-green-600">Total : {editedGroup?.lines?.length || 0} lignes — Doublons détectés : {duplicateGroups.length}</p>
                            </div>
                            <div className="flex gap-2">
                                {duplicateGroups.length > 0 && (
                                    <button
                                        onClick={() => {
                                            setCompactSelections({});
                                            setCompactMode('sum');
                                            setCompactModalOpen(true);
                                        }}
                                        className="rounded-2xl border border-green-300 bg-white px-4 py-2 text-xs font-semibold text-green-700 hover:bg-green-50"
                                    >
                                        Compacter ({duplicateGroups.length})
                                    </button>
                                )}
                                <button
                                    onClick={openAddLine}
                                    className="flex items-center gap-1.5 rounded-2xl bg-green-600 px-4 py-2 text-xs font-semibold text-white hover:bg-green-500"
                                >
                                    + Ajouter une ligne
                                </button>
                            </div>
                        </div>
                        {editedGroup?.lines && editedGroup.lines.length > 0 ? (
                            <div className="overflow-x-auto rounded-2xl border border-green-200">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-white text-xs uppercase tracking-wide text-green-700">
                                        <tr>
                                            <th className="px-4 py-3 text-left">Produit</th>
                                            <th className="px-4 py-3 text-right">Qté</th>
                                            <th className="px-4 py-3 text-left">Unité</th>
                                            <th className="px-4 py-3 text-right">Prix (Ar)</th>
                                            <th className="px-4 py-3 text-left">Fournisseur</th>
                                            <th className="px-4 py-3" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {editedGroup.lines.map((line, idx) => (
                                            <tr key={idx} className="border-t border-green-100 hover:bg-white/60">
                                                <td className="px-4 py-3 font-medium text-slate-800">
                                                    {line.itemName}
                                                    {line.perishable && (
                                                        <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-600">Périssable</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right text-slate-800">{formatLocaleNumber(line.quantity, 0)}</td>
                                                <td className="px-4 py-3 text-slate-800">{draftLineUnitLabel(line)}</td>
                                                <td className="px-4 py-3 text-right text-slate-800">{formatLocaleNumber(line.purchasePrice, 2)}</td>
                                                <td className="px-4 py-3 text-slate-800">{getSupplierDisplayName(line, suppliers, editedGroup)}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => openEditLine(idx)}
                                                            className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 hover:bg-green-200"
                                                        >
                                                            Modifier
                                                        </button>
                                                        <button
                                                            onClick={() => handleRemoveLine(idx)}
                                                            className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-200"
                                                        >
                                                            Supprimer
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="rounded-2xl border-2 border-dashed border-green-200 py-6 text-center">
                                <p className="text-sm text-green-500">Aucune ligne — cliquez sur « Ajouter une ligne »</p>
                            </div>
                        )}
                    </div>
                )}

                <div className="overflow-x-auto rounded-2xl border border-green-100">
                    <table className="min-w-full min-w-[1200px] text-sm">
                        <thead className="bg-green-50 text-xs uppercase tracking-wide text-green-700">
                            <tr>
                                <th className="px-4 py-3 text-left">Produit</th>
                                <th className="px-4 py-3 text-right">Qté désirée</th>
                                <th className="px-4 py-3 text-left">Unité</th>
                                <th className="px-4 py-3 text-right">Qté reçue</th>
                                <th className="px-4 py-3 text-right">Prix d'achat</th>
                                <th className="px-4 py-3 text-left">Fournisseur</th>
                                <th className="px-4 py-3 text-left">Date réception</th>
                                <th className="px-4 py-3 text-left">Statut</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(editMode ? editedGroup?.lines : currentGroup?.lines || group.lines).map((line) => (
                                <tr key={line.id} className="border-t border-green-100 bg-slate-50 text-slate-900">
                                    <td className="px-4 py-3 font-medium text-slate-900">
                                        {line.itemName}
                                        {line.perishable && (
                                            <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-600">Périssable</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right text-slate-900">{formatLocaleNumber(line.quantity, 0)}</td>
                                    <td className="px-4 py-3 text-slate-900">{lineUnitName(line)}</td>
                                    <td className="px-4 py-3 text-right text-slate-900">
                                        {line.status === 'reçu' ? formatLocaleNumber(line.receivedQuantity ?? line.quantity, 0) : '—'}
                                    </td>
                                    <td className="px-4 py-3 text-right text-slate-900">
                                        {line.purchasePrice !== undefined && line.purchasePrice !== null && line.purchasePrice !== ''
                                            ? formatLocaleNumber(line.purchasePrice, 2)
                                            : '—'}
                                    </td>
                                    <td className="px-4 py-3 text-slate-900">{getSupplierDisplayName(line, suppliers, currentGroup || group) || '—'}</td>
                                    <td className="px-4 py-3 text-slate-900">{formatDateTime(line.actualDeliveryDate)}</td>
                                    <td className="px-4 py-3"><StatusBadge status={line.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            </Modal>

            {compactModalOpen && (
                <Modal
                    open={compactModalOpen}
                    onClose={() => setCompactModalOpen(false)}
                    title="Compacter les lignes"
                    subtitle="Bon de commande"
                    maxWidth="max-w-lg"
                    footer={
                        <>
                            <button onClick={() => setCompactModalOpen(false)} className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                                Annuler
                            </button>
                            <button onClick={handleCompactLines} className="rounded-2xl bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-500">
                                Appliquer
                            </button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <p className="text-sm text-slate-600">
                            Les lignes identiques peuvent être regroupées en une seule ligne. Vous pouvez soit additionner les quantités, soit conserver une seule occurrence.
                        </p>
                        <Field label="Mode de regroupement">
                            <select className={selectCls} value={compactMode} onChange={(e) => setCompactMode(e.target.value)}>
                                <option value="sum">Additionner les quantités</option>
                                <option value="keep-one">Garder 1 seule ligne</option>
                            </select>
                        </Field>
                        {compactMode === 'keep-one' && duplicateGroups.length > 0 && (
                            <div className="space-y-3">
                                {duplicateGroups.map((group, groupIndex) => (
                                    <div key={`${group[0].index}-${groupIndex}`} className="rounded-2xl border border-green-200 bg-green-50/70 p-3">
                                        <p className="mb-2 text-sm font-semibold text-green-800">
                                            {group[0].line.itemName || 'Produit'}
                                        </p>
                                        <select
                                            className={selectCls}
                                            value={compactSelections[groupIndex] ?? 0}
                                            onChange={(e) => setCompactSelections((prev) => ({ ...prev, [groupIndex]: Number(e.target.value) }))}
                                        >
                                            {group.map((entry, idx) => (
                                                <option key={`${entry.index}-${idx}`} value={idx}>
                                                    Ligne {idx + 1} — Qté {formatLocaleNumber(entry.line.quantity, 0)} — {getSupplierDisplayName(entry.line) || 'Sans fournisseur'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {/* Line sub-modal for editing */}
            {lineModalOpen && (
                <LineFormModal
                    open={lineModalOpen}
                    onClose={() => setLineModalOpen(false)}
                    editingIndex={editingLineIndex}
                    existingLine={editingLineIndex !== null ? editedGroup?.lines?.[editingLineIndex] : null}
                    onSave={(line) => {
                        if (editingLineIndex !== null) handleEditLine(editingLineIndex, line);
                        else handleAddLine(line);
                    }}
                    ingredients={ingredients}
                    consumables={consumables}
                    units={units}
                    suppliers={suppliers}
                />
            )}
        </>
    );
}

// ─── Modal: Import CSV ────────────────────────────────────────────────────────

function ImportModal({ open, onClose }) {
    const [file, setFile] = useState(null);
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        if (!file) { setMessage({ type: 'error', text: 'Sélectionnez un fichier CSV.' }); return; }
        const formData = new FormData();
        formData.append('file', file);
        try {
            setLoading(true);
            setMessage({ type: 'info', text: 'Import en cours…' });
            const res = await fetch('/api/import/supply-orders', { method: 'POST', body: formData, credentials: 'same-origin' });
            const json = await res.json();
            if (!res.ok) { setMessage({ type: 'error', text: json.message || 'Erreur lors de l\'import.' }); return; }
            setMessage({ type: 'success', text: json.message || 'Import réussi.' });
            setTimeout(() => window.location.reload(), 800);
        } catch (err) {
            setMessage({ type: 'error', text: 'Erreur réseau : ' + err.message });
        } finally {
            setLoading(false);
        }
    };

    const msgCls = message?.type === 'error'
        ? 'border-red-200 bg-red-50 text-red-700'
        : message?.type === 'success'
        ? 'border-green-200 bg-green-50 text-green-700'
        : 'border-yellow-200 bg-yellow-50 text-yellow-700';

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Importer un bon de commande (CSV)"
            maxWidth="max-w-md"
            footer={
                <>
                    <button onClick={onClose} className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                        Annuler
                    </button>
                    <button onClick={submit} disabled={loading} className="rounded-2xl bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-500 disabled:opacity-50">
                        {loading ? 'Import…' : 'Importer'}
                    </button>
                </>
            }
        >
            <div className="space-y-4">
                <p className="text-sm text-slate-500">
                    Le fichier CSV doit contenir au minimum les colonnes <strong>product</strong> (ou <em>produit</em>) et <strong>quantity</strong>.
                    Colonnes optionnelles : <em>reference, date, unit, price, supplier, status</em>.
                </p>
                <Field label="Fichier CSV">
                    <input
                        type="file"
                        accept=".csv,text/csv"
                        className="w-full rounded-2xl border border-green-300 bg-green-50/60 px-4 py-2.5 text-sm text-green-900 file:mr-3 file:rounded-full file:border-0 file:bg-green-100 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-green-800"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                </Field>
                {message && (
                    <div className={`rounded-2xl border p-3 text-sm ${msgCls}`}>{message.text}</div>
                )}
            </div>
        </Modal>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SupplyTab({
    ingredients = [],
    consumables = [],
    units = [],
    supplyOrders = [],
    suppliers = [],
    onSaveSupplyOrder,
    onUpdateSupplyOrder,
    onDeleteSupplyOrder,
    onReceiveSupplyLine,
}) {
    // ── Draft order state ──
    const [draftOrder, setDraftOrder] = useState(() => ({
        reference: '',
        service: null,
        orderDate: new Date().toISOString().slice(0, 10),
        expectedDeliveryDate: '',
        supplier_id: null,
        lines: [],
    }));

    // ── Modal visibility ──
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const [receiveModalOpen, setReceiveModalOpen] = useState(false);
    const [receiveModalGroup, setReceiveModalGroup] = useState(null);
    const [receiveLines, setReceiveLines] = useState([]);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailGroup, setDetailGroup] = useState(null);
    const [importModalOpen, setImportModalOpen] = useState(false);

    // ── Filters ──
    const [filterStatus, setFilterStatus] = useState('all');

    // ── Errors ──
    const [errors, setErrors] = useState({});
    const [receiveErrors, setReceiveErrors] = useState({});

    // ── Company info and user ──
    const [companyInfo, setCompanyInfo] = useState(null);
    const [logoDataUrl, setLogoDataUrl] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);

    // ── Reference generator ──
    const generateReference = useCallback((service = null) => {
        const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const serviceSegment = (service ?? draftOrder?.service ?? '').trim().toLowerCase();
        const prefix = serviceSegment ? `BC-${serviceSegment}-${date}-` : `BC-${date}-`;
        let max = 0;
        (supplyOrders || []).forEach((o) => {
            const ref = o.lotNumber || o.lot_number || o.reference || o.ref || '';
            if (typeof ref === 'string' && ref.startsWith(prefix)) {
                const n = parseInt(ref.split('-').pop(), 10);
                if (!Number.isNaN(n) && n > max) max = n;
            }
        });
        return `${prefix}${max + 1}`;
    }, [supplyOrders, draftOrder]);

    // Init reference on mount / supplyOrders change
    useEffect(() => {
        setDraftOrder((prev) => {
            if (prev.reference) return prev;
            return { ...prev, reference: generateReference() };
        });
    }, [generateReference]);

    // ── Fetch company info and current user ──
    useEffect(() => {
        Promise.all([
            fetch('/api/company-info')
                .then(r => r.ok ? r.json() : null)
                .catch(() => null),
            fetch('/api/user')
                .then(r => r.ok ? r.json() : null)
                .catch(() => null),
        ]).then(([company, user]) => {
            setCompanyInfo(company);
            setCurrentUser(user);
        });
    }, []);

    useEffect(() => {
        const loadLogo = async () => {
            const logoSource = companyInfo?.logo_path || '/images/logo.png';
            if (!logoSource) {
                setLogoDataUrl(null);
                return;
            }

            try {
                const response = await fetch(logoSource);
                if (!response.ok) {
                    setLogoDataUrl(null);
                    return;
                }
                const blob = await response.blob();
                const bitmap = await createImageBitmap(blob);

                // Le logo est affiché en 24x24mm dans le PDF -> ~200px suffit largement (150dpi)
                const TARGET_SIZE = 200;
                const scale = Math.min(TARGET_SIZE / bitmap.width, TARGET_SIZE / bitmap.height, 1);
                const w = Math.round(bitmap.width * scale);
                const h = Math.round(bitmap.height * scale);

                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d');
                // fond blanc car on va exporter en JPEG (pas de transparence)
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, w, h);
                ctx.drawImage(bitmap, 0, 0, w, h);

                setLogoDataUrl(canvas.toDataURL('image/jpeg', 0.85));
            } catch (error) {
                console.warn('Unable to load logo image:', error);
                setLogoDataUrl(null);
            }
        };

        loadLogo();
    }, [companyInfo]);

    // ── Grouped orders ──
    // ── Helpers used by grouped/filtered memos (moved up to avoid TDZ) ──
    const normalizeLineStatus = (status) => {
        const value = String(status || '').trim().toLowerCase();
        const normalized = {
            brouillon: 'brouillon',
            commande: 'commandé',
            commandé: 'commandé',
            'en transit': 'en transit',
            en_transit: 'en transit',
            livré: 'livré',
            livre: 'livré',
            reçu: 'reçu',
            recu: 'reçu',
            annulé: 'annulé',
            annule: 'annulé',
        }[value];
        return normalized || (value || 'commandé');
    };

    const orderGroupStatus = (group) => {
        const statuses = group.lines.map((l) => normalizeLineStatus(l.status));
        if (statuses.every((status) => status === 'reçu' || status === 'annulé')) return 'reçu';
        if (statuses.every((status) => status === 'brouillon' || status === 'annulé')) return 'brouillon';
        if (statuses.some((status) => status === 'livré')) return 'livré';
        if (statuses.some((status) => status === 'en transit')) return 'en transit';
        if (statuses.some((status) => status === 'commandé')) return 'commandé';
        return 'commandé';
    };
    const groupedOrders = useMemo(() => {
        const groups = new Map();
        (supplyOrders || []).forEach((order) => {
            const reference = order.lotNumber || `CMD-${order.id}`;
            if (!groups.has(reference)) {
                // Extraire expectedDeliveryDate de n'importe quelle ligne qui l'a (pas seulement la première)
                const expectedDate = order.expectedDeliveryDate || order.expected_delivery_date;
                groups.set(reference, { 
                    reference, 
                    orderDate: order.orderDate || order.order_date || null,
                    expectedDeliveryDate: expectedDate || null,
                    lines: [] 
                });
            }
            groups.get(reference).lines.push(order);
        });
        // Passer un deuxième coup pour remplir expectedDeliveryDate manquant d'autres lignes du groupe
        for (const [ref, group] of groups) {
            if (!group.expectedDeliveryDate) {
                const lineWithDate = group.lines.find(l => l.expectedDeliveryDate || l.expected_delivery_date);
                if (lineWithDate) {
                    group.expectedDeliveryDate = lineWithDate.expectedDeliveryDate || lineWithDate.expected_delivery_date;
                }
            }
        }
        return Array.from(groups.values()).sort((a, b) => {
            if (a.orderDate && b.orderDate) return b.orderDate.localeCompare(a.orderDate);
            if (a.orderDate) return -1;
            if (b.orderDate) return 1;
            return a.reference.localeCompare(b.reference);
        });
    }, [supplyOrders]);

    const filteredOrders = useMemo(() => {
        if (filterStatus === 'all') return groupedOrders;
        return groupedOrders.filter((g) => orderGroupStatus(g) === filterStatus);
    }, [groupedOrders, filterStatus]);

    

    const lineUnitName = (line) => {
        if (line.itemType === 'ingredient') {
            const ing = ingredients.find((i) => i.id === Number(line.targetId));
            return ing?.unit?.name || ing?.unit || line.unit || 'unité';
        }
        const u = units.find((u) => u.id === Number(line.unit_id));
        return u?.name || line.unit || 'unité';
    };

    const draftLineUnitLabel = (line) => {
        if (line.itemType === 'ingredient') {
            const ing = ingredients.find((i) => i.id === Number(line.targetId));
            return ing?.unit?.name || ing?.unit || 'unité';
        }
        return units.find((u) => u.id === Number(line.unit_id))?.name || 'unité';
    };

    // ── Draft order actions ──
    const openNewOrder = () => {
        setDraftOrder({ reference: generateReference(), service: null, orderDate: new Date().toISOString().slice(0, 10), expectedDeliveryDate: '', supplier_id: null, lines: [] });
        setErrors({});
        setOrderModalOpen(true);
    };

    const handleAddLine = (line) => {
        setDraftOrder((p) => ({ ...p, lines: [...p.lines, line] }));
    };

    const handleEditLine = (idx, line) => {
        setDraftOrder((p) => {
            const lines = [...p.lines];
            lines[idx] = line;
            return { ...p, lines };
        });
    };

    const handleRemoveLine = (idx) => {
        setDraftOrder((p) => ({ ...p, lines: p.lines.filter((_, i) => i !== idx) }));
    };

    // ── Build & download purchase order PDF ──
    const buildOrderPdf = (group) => {
        const reference = group?.reference || '';
        const orderDate = group?.orderDate || group?.order_date || '';
        const expectedDeliveryDate = group?.expectedDeliveryDate || group?.expected_delivery_date || null;
        const lines = group?.lines || [];
        console.log('[buildOrderPdf] Group data:', { reference, orderDate, expectedDeliveryDate, linesCount: lines?.length });
        console.log('[buildOrderPdf] Full group:', group);
        // Try to derive validator info from group lines (group object doesn't carry those properties)
        const validatedAt = group?.validatedAt || group?.validated_at || (group?.lines && group.lines.find(l => l.validated_at)?.validated_at) || null;
        const validatedByUserId = group?.validatedByUserId || group?.validated_by_user_id || (group?.lines && group.lines.find(l => l.validated_by_user_id)?.validated_by_user_id) || null;
        let validatedByName = group?.validatedByUserName || group?.validated_by_user_name || (group?.lines && group.lines.find(l => l.validated_by_user_name)?.validated_by_user_name) || null;
        if (!validatedByName && validatedByUserId && currentUser && Number(currentUser.id) === Number(validatedByUserId)) {
            validatedByName = currentUser.name;
        }
        // Fallback to showing the id when name not available
        if (!validatedByName && validatedByUserId) validatedByName = `ID:${validatedByUserId}`;

        let formattedDate = '—';
        if (orderDate) {
            const date = new Date(orderDate);
            formattedDate = Number.isNaN(date.getTime())
                ? String(orderDate)
                : date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        }

        const company = companyInfo || { name: 'Plan-b', address: 'Tana water Front cours carré' };
        const supplierName = getSupplierDisplayName(lines?.[0]);
        const supplierInfo = suppliers?.find(s => (s.companyName || s.company_name) === supplierName);

        const infoLines = [
            { label: 'Société :', value: company?.name || 'Plan-b' },
            { label: 'Adresse société :', value: `${company?.address || ''} ${company?.city || ''}`.trim() || '—' },
            { label: 'NIF/STAT société :', value: `${company?.nif || '—'}/${company?.stat || '—'}` },
            { label: 'Fournisseur :', value: supplierName },
            { label: 'Adresse fournisseur :', value: `${supplierInfo?.address || ''} ${supplierInfo?.city || ''}`.trim() || '—' },
            { label: 'NIF/STAT fournisseur :', value: `${supplierInfo?.nif || '—'}/${supplierInfo?.stat || '—'}` },
            { label: 'Référence :', value: reference },
            { label: 'Date de commande :', value: formattedDate },
        ];
        if (expectedDeliveryDate && String(expectedDeliveryDate).trim()) {
            try {
                const expectedDate = new Date(expectedDeliveryDate);
                if (!Number.isNaN(expectedDate.getTime())) {
                    const formattedExpectedDate = expectedDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    infoLines.push({ label: 'Date de livraison attendue :', value: formattedExpectedDate });
                }
            } catch (e) {
                console.warn('Erreur lors du formatage de la date de livraison:', e);
            }
        }
        if (validatedAt && validatedByName) {
            const valDate = new Date(validatedAt);
            const formattedValidationDate = valDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
            infoLines.push({ label: 'Validé par :', value: `${validatedByName} — ${formattedValidationDate}` });
        }

        const doc = createPdfDocument();
        const startY = addPdfHeader(doc, 'Bon de commande', infoLines, logoDataUrl);

        // Vérifier si tous les fournisseurs sont identiques
        const allSuppliers = lines.map((line) => getSupplierDisplayName(line));
        const uniqueSuppliers = new Set(allSuppliers);
        const showSupplierColumn = uniqueSuppliers.size > 1;

        let columns;
        if (showSupplierColumn) {
            columns = [
                { header: 'Produit', width: 50, align: 'left' },
                { header: 'Qté', width: 14, align: 'right' },
                { header: 'Unité', width: 16, align: 'left' },
                { header: 'Prix (Ar)', width: 26, align: 'right' },
                { header: 'Fournisseur', width: 50, align: 'left' },
                { header: 'Statut', width: 20, align: 'left' },
            ];
        } else {
            columns = [
                { header: 'Produit', width: 60, align: 'left' },
                { header: 'Qté', width: 16, align: 'right' },
                { header: 'Unité', width: 18, align: 'left' },
                { header: 'Prix (Ar)', width: 30, align: 'right' },
                { header: 'Statut', width: 26, align: 'left' },
            ];
        }

        let totalPrice = 0;
        const rows = lines.map((line) => {
            const price = parseLocaleNumber(line.purchasePrice ?? line.purchase_price ?? 0);
            totalPrice += price;
            const supplierNameCol = getSupplierDisplayName(line);
            
            if (showSupplierColumn) {
                return [
                    line.itemName || '',
                    formatPdfNumber(line.quantity, 0),
                    lineUnitNameForPdf(line),
                    formatPdfNumber(price, 2),
                    supplierNameCol,
                    statusLabels[normalizeLineStatusForPdf(line.status)] || line.status || '',
                ];
            } else {
                return [
                    line.itemName || '',
                    formatPdfNumber(line.quantity, 0),
                    lineUnitNameForPdf(line),
                    formatPdfNumber(price, 2),
                    statusLabels[normalizeLineStatusForPdf(line.status)] || line.status || '',
                ];
            }
        });

        const afterTableY = addPdfTable(doc, { startY, columns, rows });
        const totalColumnIndex = showSupplierColumn ? 3 : 3;
        const afterTotalY = addPdfColumnTotal(doc, {
            y: afterTableY,
            columns,
            totalColumnIndex,
            value: formatPdfNumber(totalPrice, 2),
        });

        // Add signatures section (ordre : Validateur, Direction, Achat — sans le nom du validateur)
        const signatures = [
            { label: 'Signature Validateur', sublabel: '' },
            { label: 'Signature Direction', sublabel: company?.director_name || '' },
            { label: 'Signature Achat', sublabel: company?.purchasing_manager_name || '' },
        ];
        const afterSignaturesY = addPdfSignatures(doc, afterTotalY + 8, signatures);

        addPdfFooterNote(doc, afterSignaturesY, 'Bon de commande généré automatiquement. Les lignes réceptionnées sont indiquées dans la colonne Statut.');

        return doc;
    };

    const downloadPurchaseOrder = (group) => {
        const reference = group?.reference || 'commande';
        try {
            const doc = buildOrderPdf(group);
            if (!doc) {
                console.error('[SupplyTab] Erreur: PDF non généré');
                return;
            }
            doc.save(`bon-de-commande-${reference}.pdf`);
        } catch (err) {
            console.error('[SupplyTab] downloadPurchaseOrder error:', err);
            alert('Erreur lors de la génération du PDF: ' + (err?.message || 'Erreur inconnue'));
        }
    };

    // Accept a group object as first param (to include receiver info) or (reference, orderDate, lines)
    const buildReceivePdf = (groupOrReference, maybeOrderDate, maybeLines) => {
        let reference = '';
        let orderDate = null;
        let expectedDeliveryDate = null;
        let lines = [];
        let receiverName = null;

        let validatedByName = null;

        if (groupOrReference && typeof groupOrReference === 'object' && groupOrReference.reference) {
            const group = groupOrReference;
            reference = group.reference || '';
            orderDate = group.orderDate || group.order_date || null;
            expectedDeliveryDate = group.expectedDeliveryDate || group.expected_delivery_date || null;
            lines = maybeOrderDate || [];
            receiverName = group?.receivedByUserName || group?.received_by_user_name || (group?.lines && group.lines.find(l => l.received_by_user_name)?.received_by_user_name) || null;
            const receiverId = group?.receivedByUserId || group?.received_by_user_id || (group?.lines && group.lines.find(l => l.received_by_user_id)?.received_by_user_id) || null;
            if (!receiverName && receiverId && currentUser && Number(currentUser.id) === Number(receiverId)) {
                receiverName = currentUser.name;
            }
            if (!receiverName && receiverId) receiverName = `ID:${receiverId}`;

            validatedByName = group?.validatedByUserName || group?.validated_by_user_name || (group?.lines && group.lines.find(l => l.validated_by_user_name)?.validated_by_user_name) || null;
            const validatedByUserId = group?.validatedByUserId || group?.validated_by_user_id || (group?.lines && group.lines.find(l => l.validated_by_user_id)?.validated_by_user_id) || null;
            if (!validatedByName && validatedByUserId && currentUser && Number(currentUser.id) === Number(validatedByUserId)) {
                validatedByName = currentUser.name;
            }
            if (!validatedByName && validatedByUserId) validatedByName = `ID:${validatedByUserId}`;
        } else {
            reference = groupOrReference || '';
            orderDate = maybeOrderDate || null;
            lines = maybeLines || [];
        }

        let formattedDate = '—';
        if (orderDate) {
            const date = new Date(orderDate);
            formattedDate = Number.isNaN(date.getTime())
                ? String(orderDate)
                : date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        }

        const company = companyInfo || { name: 'Plan-b', address: 'Tana water Front cours carré' };
        // Get first supplier from lines if available
        const supplierName = getSupplierDisplayName(lines?.[0]);
        const supplierInfo = suppliers?.find(s => (s.companyName || s.company_name) === supplierName);

        const doc = createPdfDocument();
        const infoLines = [
            { label: 'Société :', value: company?.name || 'Plan-b' },
            { label: 'Adresse :', value: company?.address || 'Tana water Front cours carré' },
            { label: 'Ville :', value: company?.city || '' },
            { label: 'NIF :', value: company?.nif || '—' },
            { label: 'STAT :', value: company?.stat || '—' },
            { label: 'Fournisseur :', value: supplierName },
        ];
        
        if (supplierInfo) {
            if (supplierInfo.address || supplierInfo.city) {
                infoLines.push({ label: 'Adresse fournisseur :', value: `${supplierInfo.address || ''} ${supplierInfo.city || ''}`.trim() || '—' });
            }
            if (supplierInfo.nif || supplierInfo.stat) {
                infoLines.push({ label: 'NIF/STAT fournisseur :', value: `${supplierInfo.nif || '—'}/${supplierInfo.stat || '—'}` });
            }
        }

        if (validatedByName) {
            infoLines.push({ label: 'Validé par :', value: validatedByName });
        }
        
        infoLines.push(
            { label: 'Référence :', value: reference || '' },
            { label: 'Date de commande :', value: formattedDate }
        );
        
        if (expectedDeliveryDate) {
            const expectedDate = new Date(expectedDeliveryDate);
            const formattedExpectedDate = expectedDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
            infoLines.push({ label: 'Date de livraison attendue :', value: formattedExpectedDate });
        }
        
        if (receiverName) {
            infoLines.push({ label: 'Réceptionné par :', value: receiverName });
        }

        const startY = addPdfHeader(doc, 'Bon de réception', infoLines, logoDataUrl);

        const columns = [
            { header: 'Produit', width: 35, align: 'left' },
            { header: 'Qté reçue', width: 18, align: 'right' },
            { header: 'Unité', width: 16, align: 'left' },
            { header: 'Fournisseur', width: 30, align: 'left' },
            { header: 'Prix d\'achat (Ar)', width: 28, align: 'right' },
            { header: 'Date réception', width: 28, align: 'left' },
        ];

        const receivedLines = (lines || []).filter((line) => normalizeLineStatusForPdf(line.status) === 'reçu');

        let totalPrice = 0;
        const rows = receivedLines.map((line) => {
            const price = parseLocaleNumber(line.purchasePrice ?? line.purchase_price ?? 0);
            totalPrice += price;
            return [
                line.itemName || '',
                formatPdfNumber(line.receivedQuantity ?? line.quantity ?? 0, 0),
                lineUnitNameForPdf(line),
                getSupplierDisplayName(line),
                formatPdfNumber(price, 2),
                formatDateTime(line.actualDeliveryDate ?? line.actual_delivery_date),
            ];
        });

        const afterTableY = addPdfTable(doc, { startY, columns, rows });
        const afterTotalY = addPdfColumnTotal(doc, {
            y: afterTableY,
            columns,
            totalColumnIndex: 4,
            value: formatPdfNumber(totalPrice, 2),
        });

        // Add signatures section
        const signatures = [
            { label: 'Signature Direction', sublabel: company?.director_name || '' },
            { label: 'Signature Achat', sublabel: company?.purchasing_manager_name || '' },
            { label: 'Signature Réceptionnaire', sublabel: receiverName ? `${receiverName}` : '' },
        ];
        const afterSignaturesY = addPdfSignatures(doc, afterTotalY + 8, signatures);

        addPdfFooterNote(doc, afterSignaturesY, 'Bon de réception généré automatiquement. Liste des produits réceptionnés.');

        return doc;
    };

    const downloadReceiveDocument = (groupOrReference, maybeOrderDateOrLines, maybeLines) => {
        try {
            const doc = buildReceivePdf(groupOrReference, maybeOrderDateOrLines, maybeLines);
            const reference = (groupOrReference && typeof groupOrReference === 'object' && groupOrReference.reference) ? (groupOrReference.reference || 'reception') : (groupOrReference || 'reception');
            doc.save(`bon-de-reception-${reference}.pdf`);
        } catch (err) {
            console.error('[SupplyTab] downloadReceiveDocument error:', err);
        }
    };

    const printSupplyGroup = (group) => {
        const state = orderGroupStatus(group);
        if (state === 'reçu') {
            return downloadReceiveDocument(group, group.lines);
        }
        return downloadPurchaseOrder(group);
    };

    const validateDraftLine = (line, defaultSupplierName = '') => {
        const quantity = Number(parseLocaleNumber(line.quantity));
        const price = line.purchasePrice === '' || line.purchasePrice === null || line.purchasePrice === undefined
            ? null
            : Number(parseLocaleNumber(line.purchasePrice));
        if (!line.itemName?.trim()) return 'Chaque ligne doit contenir un produit.';
        if (!Number.isFinite(quantity) || quantity <= 0) return 'Chaque ligne doit contenir une quantité valide.';
        if (price !== null && (!Number.isFinite(price) || price < 0)) return 'Chaque ligne doit contenir un prix valide.';
        if (!getSupplierDisplayName(line).trim() && !defaultSupplierName.trim()) return 'Chaque ligne doit contenir un fournisseur valide.';
        return null;
    };

    const validateDraftOrder = async () => {
        if (!draftOrder.lines.length) {
            setErrors({ _global: ['Ajoutez au moins une ligne avant de valider le bon de commande.'] });
            return;
        }

        const defaultSupplierName = draftOrder.supplier_id
            ? suppliers.find((s) => Number(s.id) === Number(draftOrder.supplier_id))?.companyName || suppliers.find((s) => Number(s.id) === Number(draftOrder.supplier_id))?.company_name || ''
            : '';
        const invalidLine = draftOrder.lines.find((line) => validateDraftLine(line, defaultSupplierName));

        if (invalidLine) {
            setErrors({ _global: [validateDraftLine(invalidLine, defaultSupplierName)] });
            return;
        }

        const reference = draftOrder.reference || generateReference();
        const orderDate = draftOrder.orderDate || new Date().toISOString().slice(0, 10);
        const expectedDeliveryDate = draftOrder.expectedDeliveryDate?.trim() || null;
        const now = new Date().toISOString().split('T')[0];
        const validatedByUserId = currentUser?.id || null;
        const lines = draftOrder.lines.map((l) => ({
            ...l,
            supplier: l.supplier?.trim() || defaultSupplierName || l.supplier_name || l.companyName || l.company_name || '',
            reference,
            service: draftOrder.service ?? l.service ?? null,
            orderDate,
            expectedDeliveryDate,
            status: 'commandé',
            supplier_id: draftOrder.supplier_id || l.supplier_id || null,
            lotNumber: reference,
            validatedByUserId,
            validatedAt: now,
        }));
        try {
            setErrors({});
            const created = await Promise.all(lines.map(async (l, idx) => {
                try {
                    // If line has ID, it already exists in DB (as draft) — update it to 'commandé'
                    if (l.id) {
                        return await onUpdateSupplyOrder(l.id, l);
                    }
                    // Otherwise create new
                    return await onSaveSupplyOrder(l);
                } catch (err) {
                    const lineMessage = err?.message ? `Ligne ${idx + 1}: ${err.message}` : `Ligne ${idx + 1}: erreur lors de la sauvegarde`;
                    throw new Error(lineMessage);
                }
            }));
            downloadPurchaseOrder({ reference, orderDate, expectedDeliveryDate, lines: created, validatedByUserName: currentUser?.name, validatedAt: now });
            console.log('[validateDraftOrder] Appel downloadPurchaseOrder avec:', { reference, orderDate, expectedDeliveryDate, validatedByUserName: currentUser?.name, validatedAt: now, createdCount: created?.length });
            setOrderModalOpen(false);
            setDraftOrder({ reference: generateReference(), orderDate: new Date().toISOString().slice(0, 10), expectedDeliveryDate: '', supplier_id: null, lines: [] });
        } catch (err) {
            console.error(err);
            let messages = ['Impossible de valider le bon de commande. Vérifiez les informations saisies.'];
            if (err?.body) {
                try {
                    const parsed = JSON.parse(err.body);
                    if (parsed?.errors) {
                        messages = Object.values(parsed.errors).flat().map(String);
                    } else if (parsed?.message) {
                        messages = [parsed.message];
                    }
                } catch (e) {
                    // ignore
                }
            } else if (err?.message) {
                messages = [err.message];
            }
            setErrors({ _global: messages });
        }
    };

    const saveDraftOrder = async () => {
        if (!draftOrder.lines.length) {
            setErrors({ _global: ['Ajoutez au moins une ligne avant d’enregistrer le brouillon.'] });
            return;
        }

        const defaultSupplierName = draftOrder.supplier_id
            ? suppliers.find((s) => Number(s.id) === Number(draftOrder.supplier_id))?.companyName || suppliers.find((s) => Number(s.id) === Number(draftOrder.supplier_id))?.company_name || ''
            : '';
        const invalidLine = draftOrder.lines.find((line) => validateDraftLine(line, defaultSupplierName));

        if (invalidLine) {
            setErrors({ _global: [validateDraftLine(invalidLine, defaultSupplierName)] });
            return;
        }

        const reference = draftOrder.reference || generateReference();
        const orderDate = draftOrder.orderDate || new Date().toISOString().slice(0, 10);
        const expectedDeliveryDate = draftOrder.expectedDeliveryDate?.trim() || null;
        const lines = draftOrder.lines.map((l) => ({
            ...l,
            supplier: l.supplier?.trim() || defaultSupplierName || l.supplier_name || l.companyName || l.company_name || '',
            reference,
            service: draftOrder.service ?? l.service ?? null,
            orderDate,
            expectedDeliveryDate,
            status: 'brouillon',
            supplier_id: draftOrder.supplier_id || l.supplier_id || null,
            lotNumber: reference,
        }));
        try {
            setErrors({});
            await Promise.all(lines.map(async (l, idx) => {
                try {
                    return await onSaveSupplyOrder(l);
                } catch (err) {
                    const message = err?.body ? err.body : err?.message || 'Erreur inconnue';
                    throw new Error(`Ligne ${idx + 1}: ${message}`);
                }
            }));
            setOrderModalOpen(false);
            setDraftOrder({ reference: generateReference(), orderDate: new Date().toISOString().slice(0, 10), expectedDeliveryDate: '', supplier_id: null, lines: [] });
        } catch (err) {
            console.error(err);
            let messages = ['Impossible d\u2019enregistrer le brouillon. Vérifiez les informations saisies.'];
            if (err?.body) {
                try {
                    const parsed = JSON.parse(err.body);
                    if (parsed?.errors) {
                        messages = Object.values(parsed.errors).flat().map(String);
                    } else if (parsed?.message) {
                        messages = [parsed.message];
                    }
                } catch (e) {
                    // ignore
                }
            } else if (err?.message) {
                messages = [err.message];
            }
            setErrors({ _global: messages });
        }
    };

    // ── Validate existing draft order ──
    const validateExistingDraftOrder = async (group) => {
        if (!group?.lines?.length) {
            setErrors({ _global: ['Aucune ligne à valider.'] });
            return;
        }

        const invalidLine = group.lines.find((line) => {
            const defaultSupplierName = line.supplier?.trim()
                || (line.supplier_id ? suppliers.find((s) => Number(s.id) === Number(line.supplier_id))?.companyName || suppliers.find((s) => Number(s.id) === Number(line.supplier_id))?.company_name || '' : '');
            return validateDraftLine(line, defaultSupplierName);
        });
        if (invalidLine) {
            const defaultSupplierName = invalidLine.supplier?.trim()
                || (invalidLine.supplier_id ? suppliers.find((s) => Number(s.id) === Number(invalidLine.supplier_id))?.companyName || suppliers.find((s) => Number(s.id) === Number(invalidLine.supplier_id))?.company_name || '' : '');
            setErrors({ _global: [validateDraftLine(invalidLine, defaultSupplierName)] });
            return;
        }

        try {
            setErrors({});
            const now = new Date().toISOString().split('T')[0]; // Current date
            const validatedByUserId = currentUser?.id || null;
            // Update all lines from brouillon to commandé
            await Promise.all(
                group.lines.map(async (line, idx) => {
                    try {
                        return await onUpdateSupplyOrder(line.id, { 
                            ...line, 
                            status: 'commandé',
                            validatedAt: now,
                            validatedByUserId,
                        });
                    } catch (err) {
                        const lineMessage = err?.message ? `Ligne ${idx + 1}: ${err.message}` : `Ligne ${idx + 1}: erreur lors de la mise à jour`;
                        throw new Error(lineMessage);
                    }
                })
            );
            downloadPurchaseOrder({ ...group, validatedByUserName: currentUser?.name, validatedAt: now });
        } catch (err) {
            console.error(err);
            let messages = ['Impossible de valider le bon de commande. Vérifiez les informations saisies.'];
            if (err?.body) {
                try {
                    const parsed = JSON.parse(err.body);
                    if (parsed?.errors) {
                        messages = Object.values(parsed.errors).flat().map(String);
                    } else if (parsed?.message) {
                        messages = [parsed.message];
                    }
                } catch (e) {
                    // ignore
                }
            } else if (err?.message) {
                messages = [err.message];
            }
            setErrors({ _global: messages });
        }
    };

    // ── Receive modal ──
    const openReceiveModal = (group) => {
        const today = toDateTimeLocalValue(new Date().toISOString());
        const lines = group.lines
            .filter((l) => !['reçu', 'annulé'].includes(l.status))
            .map((l) => ({
                id: l.id,
                itemName: l.itemName || l.item_name,
                desiredQuantity: l.quantity,
                receivedQuantity: l.receivedQuantity ?? l.received_quantity ?? l.quantity,
                purchasePrice: l.purchasePrice ?? l.purchase_price ?? '',
                actualDeliveryDate: toDateTimeLocalValue(l.actualDeliveryDate ?? l.actual_delivery_date) || today,
                supplier: l.supplier ?? l.supplier_name ?? l.companyName ?? l.company_name ?? l.provider ?? '',
                status: 'pending',
            }));
        setReceiveLines(lines);
        setReceiveModalGroup(group);
        setReceiveErrors({});
        setReceiveModalOpen(true);
    };

    const updateReceiveLine = (idx, key, value) => {
        setReceiveLines((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], [key]: value };
            return next;
        });
    };

    const submitReceiveModal = async () => {
        const canMark = (line) =>
            line.receivedQuantity !== '' &&
            Number(parseLocaleNumber(line.receivedQuantity)) > 0 &&
            line.purchasePrice !== '' &&
            !Number.isNaN(parseLocaleNumber(line.purchasePrice)) &&
            Boolean(line.actualDeliveryDate);

        if (receiveLines.find((l) => l.status === 'reçu' && !canMark(l))) {
            setReceiveErrors({ _global: ['Chaque ligne marquée reçu doit avoir une quantité, un prix et une date valides.'] });
            return;
        }
        if (receiveLines.every((l) => l.status === 'pending')) {
            setReceiveErrors({ _global: ['Aucune ligne sélectionnée pour la réception.'] });
            return;
        }
        try {
            const printedLines = receiveLines.filter((line) => line.status === 'reçu');
            for (const line of receiveLines) {
                if (line.status === 'pending') continue;
                if (line.status === 'reçu') {
                    const payload = {
                        receivedQuantity: Number(parseLocaleNumber(line.receivedQuantity)),
                        actualDeliveryDate: line.actualDeliveryDate,
                        purchasePrice: parseLocaleNumber(line.purchasePrice),
                        status: 'reçu',
                    };
                    if (typeof onReceiveSupplyLine === 'function') {
                        await onReceiveSupplyLine(line.id, payload);
                    } else {
                        await onUpdateSupplyOrder(line.id, { status: 'reçu', actualDeliveryDate: payload.actualDeliveryDate, purchasePrice: payload.purchasePrice, quantity: payload.receivedQuantity });
                    }
                } else if (line.status === 'annulé') {
                    await onUpdateSupplyOrder(line.id, { status: 'annulé' });
                }
            }
            setReceiveModalOpen(false);
            setReceiveLines([]);
                if (printedLines.length) {
                downloadReceiveDocument(receiveModalGroup, printedLines);
            }
            setReceiveModalGroup(null);
        } catch (err) {
            console.error(err);
            setReceiveErrors({ _global: ['Erreur lors de la réception des lignes.'] });
        }
    };

    // ── Filter tabs ──
    const filterTabs = [
        { key: 'all', label: 'Tous', count: groupedOrders.length },
        { key: 'brouillon', label: 'Brouillon' },
        { key: 'commandé', label: 'Commandé' },
        { key: 'en transit', label: 'En transit' },
        { key: 'livré', label: 'Livré' },
        { key: 'reçu', label: 'Reçu' },
    ].map((t) => ({
        ...t,
        count: t.count ?? groupedOrders.filter((g) => orderGroupStatus(g) === t.key).length,
    }));

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6">
            {/* Header */}
            <section className="flex flex-col gap-4 rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl shadow-slate-950/20 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <p className="text-xs uppercase tracking-[0.35em] text-green-500">Approvisionnement</p>
                    <h2 className="text-2xl font-semibold text-green-900">Gestion des bons de commande</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setImportModalOpen(true)}
                        className="rounded-2xl border border-green-300 bg-white px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-50"
                    >
                        Importer CSV
                    </button>
                    <button
                        onClick={openNewOrder}
                        className="rounded-2xl bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-500"
                    >
                        + Nouveau bon
                    </button>
                </div>
            </section>

            {/* Filter bar */}
            <div className="flex flex-wrap gap-2">
                {filterTabs.map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => setFilterStatus(tab.key)}
                        className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition ${filterStatus === tab.key ? 'bg-green-600 text-white' : 'bg-white border border-green-200 text-green-700 hover:bg-green-50'}`}
                    >
                        {tab.label}
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${filterStatus === tab.key ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700'}`}>
                            {tab.count}
                        </span>
                    </button>
                ))}
            </div>

            {/* Orders list */}
            {filteredOrders.length === 0 ? (
                <div className="rounded-3xl border-2 border-dashed border-green-200 py-16 text-center">
                    <p className="text-green-500">Aucun bon de commande{filterStatus !== 'all' ? ` avec le statut « ${statusLabels[filterStatus]} »` : ''}.</p>
                    <button onClick={openNewOrder} className="mt-4 rounded-2xl bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-500">
                        Créer un bon de commande
                    </button>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-3xl border border-green-200 bg-white shadow-sm">
                    <table className="min-w-full text-sm">
                        <thead className="bg-green-50 text-xs uppercase tracking-wide text-green-700">
                            <tr>
                                <th className="px-5 py-3 text-left">Référence</th>
                                <th className="px-5 py-3 text-left">Date</th>
                                <th className="px-5 py-3 text-right">Lignes</th>
                                <th className="px-5 py-3 text-left">Statut</th>
                                <th className="px-5 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.map((group) => {
                                const state = orderGroupStatus(group);
                                return (
                                    <tr
                                        key={group.reference}
                                        className="cursor-pointer border-t border-green-100 hover:bg-green-50/60"
                                        onClick={() => { setDetailGroup(group); setDetailModalOpen(true); }}
                                    >
                                        <td className="px-5 py-3 font-semibold text-green-900">{group.reference}</td>
                                        <td className="px-5 py-3 text-slate-500">
                                            {group.orderDate ? new Date(group.orderDate).toLocaleDateString('fr-FR') : '—'}
                                        </td>
                                        <td className="px-5 py-3 text-right text-slate-600">{group.lines.length}</td>
                                        <td className="px-5 py-3"><StatusBadge status={state} /></td>
                                        <td className="px-5 py-3 text-right">
                                            <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                                {state !== 'reçu' && state !== 'brouillon' && group.lines.some((l) => !['reçu', 'annulé'].includes(l.status)) && (
                                                    <button
                                                        onClick={() => openReceiveModal(group)}
                                                        className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800 hover:bg-green-200"
                                                    >
                                                        Réceptionner
                                                    </button>
                                                )}
                                                {(state === 'commandé' || state === 'reçu') && (
                                                    <button
                                                        onClick={() => printSupplyGroup(group)}
                                                        className="rounded-full border border-green-200 bg-white px-3 py-1 text-xs font-medium text-green-700 hover:bg-green-50"
                                                    >
                                                        Imprimer
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => { setDetailGroup(group); setDetailModalOpen(true); }}
                                                    className="rounded-full border border-green-200 bg-white px-3 py-1 text-xs font-medium text-green-700 hover:bg-green-50"
                                                >
                                                    Voir
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Modals ── */}

            <OrderFormModal
                open={orderModalOpen}
                onClose={() => setOrderModalOpen(false)}
                draftOrder={draftOrder}
                setDraftOrder={setDraftOrder}
                onAddLine={handleAddLine}
                onEditLine={handleEditLine}
                onRemoveLine={handleRemoveLine}
                onValidate={validateDraftOrder}
                onSaveDraft={saveDraftOrder}
                errors={errors}
                draftLineUnitLabel={draftLineUnitLabel}
                generateReference={generateReference}
                ingredients={ingredients}
                consumables={consumables}
                units={units}
                suppliers={suppliers}
            />

            <ReceiveModal
                open={receiveModalOpen}
                onClose={() => setReceiveModalOpen(false)}
                group={receiveModalGroup}
                receiveLines={receiveLines}
                onUpdateLine={updateReceiveLine}
                onSubmit={submitReceiveModal}
                errors={receiveErrors}
            />

            <DetailModal
                open={detailModalOpen}
                onClose={() => setDetailModalOpen(false)}
                group={detailGroup}
                orderGroupStatus={orderGroupStatus}
                lineUnitName={lineUnitName}
                onReceive={openReceiveModal}
                onValidateDraft={validateExistingDraftOrder}
                onUpdateSupplyOrder={onUpdateSupplyOrder}
                onSaveSupplyOrder={onSaveSupplyOrder}
                onDeleteSupplyOrder={onDeleteSupplyOrder}
                ingredients={ingredients}
                consumables={consumables}
                units={units}
                suppliers={suppliers}
            />

            <ImportModal
                open={importModalOpen}
                onClose={() => setImportModalOpen(false)}
            />
        </div>
    );
}